'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function BondCalculator() {
  const t = useTranslations("tools.bondCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator 1 state
  const [calc1, setCalc1] = useState({
    price: '',
    faceValue: '100',
    yield: '',
    years: '',
    coupon: '5',
    couponUnit: 'percent',
    frequency: '2'
  });
  const [results1, setResults1] = useState<any>(null);
  const [error1, setError1] = useState('');

  // Calculator 2 state
  const [calc2, setCalc2] = useState({
    faceValue: '100',
    yield: '6',
    coupon: '5',
    couponUnit: 'percent',
    frequency: '2',
    maturityDate: '',
    settlementDate: '',
    dayCount: '30/360'
  });
  const [results2, setResults2] = useState<any>(null);
  const [error2, setError2] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  useEffect(() => {
    const today = new Date();
    const futureDate = new Date(today);
    futureDate.setFullYear(today.getFullYear() + 3);
    setCalc2(prev => ({
      ...prev,
      maturityDate: futureDate.toISOString().split('T')[0],
      settlementDate: today.toISOString().split('T')[0]
    }));
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        marginBottom: '2rem',
        borderRadius: '1rem'
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolHeader: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '1.5rem' },
      {},
      { marginBottom: '2rem' },
      { marginBottom: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    calculatorGrid: getResponsiveStyle(
      { display: 'grid', gap: '1.5rem', marginBottom: '2rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '3rem' }
    ),
    calculatorCard: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    cardTitle: getResponsiveStyle(
      {
        color: '#2563eb',
        marginBottom: '1.5rem',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.25rem' }
    ),
    formGroup: {
      marginBottom: '1.25rem'
    },
    label: getResponsiveStyle(
      {
        display: 'block',
        fontWeight: 500,
        color: '#334155',
        marginBottom: '0.5rem'
      },
      { fontSize: '0.875rem' },
      { fontSize: '0.95rem' },
      { fontSize: '0.95rem' }
    ),
    input: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box' as const
      },
      { padding: '0.75rem' },
      { padding: '0.75rem' },
      { padding: '0.75rem' }
    ),
    select: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box' as const,
        background: 'white'
      },
      { padding: '0.75rem' },
      { padding: '0.75rem' },
      { padding: '0.75rem' }
    ),
    inputWithUnit: {
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      gap: '0.5rem'
    },
    unitSelect: {
      width: '80px'
    },
    radioGroup: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '0.75rem',
      paddingLeft: '0.5rem'
    },
    radioOption: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    buttonGroup: getResponsiveStyle(
      { display: 'flex', gap: '1rem', marginTop: '1.5rem' },
      { flexDirection: 'column' as const },
      { flexDirection: 'row' as const },
      { flexDirection: 'row' as const }
    ),
    calculateBtn: getResponsiveStyle(
      {
        flex: 1,
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white'
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem', minHeight: '44px' },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' }
    ),
    clearBtn: getResponsiveStyle(
      {
        flex: 1,
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        background: '#f1f5f9',
        color: '#475569'
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem', minHeight: '44px' },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' }
    ),
    results: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        marginBottom: '2rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    resultHighlight: {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderLeft: '4px solid #2563eb',
      padding: '1.5rem',
      borderRadius: '0.5rem',
      marginBottom: '1.5rem'
    },
    resultValue: getResponsiveStyle(
      {
        fontWeight: 700,
        color: '#2563eb',
        display: 'block',
        marginTop: '0.5rem'
      },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '1.75rem' }
    ),
    resultTable: {
      width: '100%',
      borderCollapse: 'collapse' as const
    },
    resultTableRow: {
      borderBottom: '1px solid #e2e8f0'
    },
    resultTableCell: getResponsiveStyle(
      { padding: '1rem 0' },
      { fontSize: '0.875rem' },
      { fontSize: '0.95rem' },
      { fontSize: '0.95rem' }
    ),
    errorMessage: {
      background: '#fef2f2',
      borderLeft: '4px solid #ef4444',
      padding: '1rem',
      borderRadius: '0.5rem',
      color: '#991b1b',
      marginTop: '1rem'
    },
    glassCard: getResponsiveStyle(
      {
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '1px solid rgba(255, 200, 180, 0.2)',
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem', lineHeight: 1.7 },
      { fontSize: '1.125rem', lineHeight: 1.8 }
    ),
    list: getResponsiveStyle(
      { paddingLeft: '1.5rem', color: colors.textSecondary, lineHeight: 1.8 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    listItem: {
      marginBottom: '0.75rem'
    },
    strong: {
      color: colors.textPrimary,
      fontWeight: 600
    },
    faqItem: {
      marginBottom: '1.5rem'
    },
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    faqAnswer: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' },
      { fontSize: '1rem' }
    ),
    refLink: {
      color: '#2563eb',
      textDecoration: 'none',
      wordBreak: 'break-word' as const
    }
  };

  const calculateBondPrice = (faceValue: number, couponRate: number, yieldRate: number, years: number, frequency: number) => {
    const n = years * frequency;
    const r = yieldRate / 100 / frequency;
    const c = (couponRate / 100 * faceValue) / frequency;

    let price = 0;
    for (let i = 1; i <= n; i++) {
      price += c / Math.pow(1 + r, i);
    }
    price += faceValue / Math.pow(1 + r, n);

    return price;
  };

  const calculateYield = (price: number, faceValue: number, couponRate: number, years: number, frequency: number) => {
    const c = (couponRate / 100 * faceValue) / frequency;
    const n = years * frequency;

    let low = 0, high = 100;
    let yieldRate = 5;

    for (let iter = 0; iter < 100; iter++) {
      const r = yieldRate / 100 / frequency;
      let calcPrice = 0;

      for (let i = 1; i <= n; i++) {
        calcPrice += c / Math.pow(1 + r, i);
      }
      calcPrice += faceValue / Math.pow(1 + r, n);

      if (Math.abs(calcPrice - price) < 0.0001) break;

      if (calcPrice > price) {
        low = yieldRate;
      } else {
        high = yieldRate;
      }
      yieldRate = (low + high) / 2;
    }

    return yieldRate;
  };

  const calculateYears = (price: number, faceValue: number, couponRate: number, yieldRate: number, frequency: number) => {
    const c = (couponRate / 100 * faceValue) / frequency;
    const r = yieldRate / 100 / frequency;

    let years = 1;
    for (let y = 0.1; y <= 50; y += 0.01) {
      const n = y * frequency;
      let calcPrice = 0;

      for (let i = 1; i <= n; i++) {
        calcPrice += c / Math.pow(1 + r, i);
      }
      calcPrice += faceValue / Math.pow(1 + r, n);

      if (Math.abs(calcPrice - price) < 0.01) {
        years = y;
        break;
      }
    }

    return years;
  };

  const daysBetween = (date1: Date, date2: Date) => {
    const oneDay = 24 * 60 * 60 * 1000;
    return Math.round(Math.abs((date1.getTime() - date2.getTime()) / oneDay));
  };

  const getDayCountFraction = (startDate: Date, endDate: Date, convention: string) => {
    const days = daysBetween(startDate, endDate);

    switch(convention) {
      case '30/360':
        return days / 360;
      case 'actual/360':
        return days / 360;
      case 'actual/365':
        return days / 365;
      case 'actual/actual': {
        const daysInYear = (new Date(endDate.getFullYear(), 11, 31).getTime() - new Date(endDate.getFullYear(), 0, 1).getTime()) / (24 * 60 * 60 * 1000) + 1;
        return days / daysInYear;
      }
      default:
        return days / 360;
    }
  };

  const handleCalculate1 = (e: React.FormEvent) => {
    e.preventDefault();
    setError1('');

    const price = calc1.price ? parseFloat(calc1.price) : null;
    const faceValue = parseFloat(calc1.faceValue);
    const yieldVal = calc1.yield ? parseFloat(calc1.yield) : null;
    const years = calc1.years ? parseFloat(calc1.years) : null;
    let coupon = parseFloat(calc1.coupon);
    const frequency = parseInt(calc1.frequency);

    if (calc1.couponUnit === 'dollar') {
      coupon = (coupon / faceValue) * 100;
    }

    const nullCount = [price, yieldVal, years].filter(v => v === null).length;

    if (nullCount !== 1) {
      setError1(t('error1'));
      return;
    }

    let result, resultLabel, resultValue;

    if (price === null) {
      result = calculateBondPrice(faceValue, coupon, yieldVal!, years!, frequency);
      resultLabel = t('priceLabel');
      resultValue = '$' + result.toFixed(4);
    } else if (yieldVal === null) {
      result = calculateYield(price, faceValue, coupon, years!, frequency);
      resultLabel = t('yieldLabel');
      resultValue = result.toFixed(4) + '%';
    } else {
      result = calculateYears(price, faceValue, coupon, yieldVal, frequency);
      resultLabel = t('yearsLabel');
      resultValue = result.toFixed(2) + ' ' + t('yearsUnit');
    }

    setResults1({ label: resultLabel, value: resultValue });
  };

  const handleCalculate2 = (e: React.FormEvent) => {
    e.preventDefault();
    setError2('');

    const faceValue = parseFloat(calc2.faceValue);
    const yieldVal = parseFloat(calc2.yield);
    let coupon = parseFloat(calc2.coupon);
    const frequency = parseInt(calc2.frequency);
    const maturityDate = new Date(calc2.maturityDate);
    const settlementDate = new Date(calc2.settlementDate);

    if (calc2.couponUnit === 'dollar') {
      coupon = (coupon / faceValue) * 100;
    }

    if (settlementDate >= maturityDate) {
      setError2(t('error2'));
      return;
    }

    const yearsToMaturity = (maturityDate.getTime() - settlementDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
    const bondPrice = calculateBondPrice(faceValue, coupon, yieldVal, yearsToMaturity, frequency);

    const couponPeriodDays = 365.25 / frequency;
    const lastCouponDate = new Date(settlementDate);
    lastCouponDate.setDate(settlementDate.getDate() - (daysBetween(settlementDate, maturityDate) % couponPeriodDays));

    const daysSinceLastCoupon = daysBetween(lastCouponDate, settlementDate);
    const accruedFraction = getDayCountFraction(lastCouponDate, settlementDate, calc2.dayCount);
    const accruedInterest = (coupon / 100 * faceValue / frequency) * accruedFraction;

    const cleanPrice = bondPrice - accruedInterest;

    setResults2({
      dirtyPrice: '$' + bondPrice.toFixed(4),
      cleanPrice: '$' + cleanPrice.toFixed(4),
      accruedInterest: '$' + accruedInterest.toFixed(4),
      accruedDays: Math.round(daysSinceLastCoupon)
    });
  };

  const handleClear1 = () => {
    setCalc1({
      price: '',
      faceValue: '100',
      yield: '',
      years: '',
      coupon: '5',
      couponUnit: 'percent',
      frequency: '2'
    });
    setResults1(null);
    setError1('');
  };

  const handleClear2 = () => {
    const today = new Date();
    const futureDate = new Date(today);
    futureDate.setFullYear(today.getFullYear() + 3);
    setCalc2({
      faceValue: '100',
      yield: '6',
      coupon: '5',
      couponUnit: 'percent',
      frequency: '2',
      maturityDate: futureDate.toISOString().split('T')[0],
      settlementDate: today.toISOString().split('T')[0],
      dayCount: '30/360'
    });
    setResults2(null);
    setError2('');
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t('title')}</h1>
        <p style={styles.summaryText}>{t('subtitle')}</p>
      </header>

      <section style={styles.calculatorTool}>
        <div style={styles.toolHeader}>
          <h2 style={styles.h2}>🧮 {t('toolTitle')}</h2>
        </div>

        <div style={styles.calculatorGrid}>
          <div style={styles.calculatorCard}>
            <h3 style={styles.cardTitle}>📊 {t('calc1Title')}</h3>
            <form onSubmit={handleCalculate1}>
              <div style={styles.formGroup}>
                <label style={styles.label}>{t('priceLabel')}</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder={t('leaveBlanktocalculate')}
                  value={calc1.price}
                  onChange={(e) => setCalc1({...calc1, price: e.target.value})}
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t('faceValueLabel')}</label>
                <input
                  type="number"
                  step="0.01"
                  value={calc1.faceValue}
                  onChange={(e) => setCalc1({...calc1, faceValue: e.target.value})}
                  required
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t('yieldLabel')}</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder={t('leaveBlanktocalculate')}
                  value={calc1.yield}
                  onChange={(e) => setCalc1({...calc1, yield: e.target.value})}
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t('yearsLabel')}</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder={t('leaveBlanktocalculate')}
                  value={calc1.years}
                  onChange={(e) => setCalc1({...calc1, years: e.target.value})}
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t('annualCouponLabel')}</label>
                <div style={styles.inputWithUnit}>
                  <input
                    type="number"
                    step="0.01"
                    value={calc1.coupon}
                    onChange={(e) => setCalc1({...calc1, coupon: e.target.value})}
                    required
                    style={styles.input}
                  />
                  <select
                    value={calc1.couponUnit}
                    onChange={(e) => setCalc1({...calc1, couponUnit: e.target.value})}
                    style={{...styles.select, ...styles.unitSelect}}
                  >
                    <option value="percent">%</option>
                    <option value="dollar">$</option>
                  </select>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t('couponFrequencyLabel')}</label>
                <select
                  value={calc1.frequency}
                  onChange={(e) => setCalc1({...calc1, frequency: e.target.value})}
                  style={styles.select}
                >
                  <option value="1">{t('annually')}</option>
                  <option value="2">{t('semiannually')}</option>
                  <option value="4">{t('quarterly')}</option>
                  <option value="12">{t('monthly')}</option>
                </select>
              </div>

              {error1 && <div style={styles.errorMessage}>{error1}</div>}

              <div style={styles.buttonGroup}>
                <button type="submit" style={styles.calculateBtn}>{t('calculateButton')}</button>
                <button type="button" onClick={handleClear1} style={styles.clearBtn}>{t('clearButton')}</button>
              </div>
            </form>
          </div>

          <div style={styles.calculatorCard}>
            <h3 style={styles.cardTitle}>📅 {t('calc2Title')}</h3>
            <form onSubmit={handleCalculate2}>
              <div style={styles.formGroup}>
                <label style={styles.label}>{t('faceValueLabel')}</label>
                <input
                  type="number"
                  step="0.01"
                  value={calc2.faceValue}
                  onChange={(e) => setCalc2({...calc2, faceValue: e.target.value})}
                  required
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t('yieldLabel')}</label>
                <input
                  type="number"
                  step="0.01"
                  value={calc2.yield}
                  onChange={(e) => setCalc2({...calc2, yield: e.target.value})}
                  required
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t('annualCouponLabel')}</label>
                <div style={styles.inputWithUnit}>
                  <input
                    type="number"
                    step="0.01"
                    value={calc2.coupon}
                    onChange={(e) => setCalc2({...calc2, coupon: e.target.value})}
                    required
                    style={styles.input}
                  />
                  <select
                    value={calc2.couponUnit}
                    onChange={(e) => setCalc2({...calc2, couponUnit: e.target.value})}
                    style={{...styles.select, ...styles.unitSelect}}
                  >
                    <option value="percent">%</option>
                    <option value="dollar">$</option>
                  </select>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t('couponFrequencyLabel')}</label>
                <select
                  value={calc2.frequency}
                  onChange={(e) => setCalc2({...calc2, frequency: e.target.value})}
                  style={styles.select}
                >
                  <option value="1">{t('annually')}</option>
                  <option value="2">{t('semiannually')}</option>
                  <option value="4">{t('quarterly')}</option>
                  <option value="12">{t('monthly')}</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t('maturityDateLabel')}</label>
                <input
                  type="date"
                  value={calc2.maturityDate}
                  onChange={(e) => setCalc2({...calc2, maturityDate: e.target.value})}
                  required
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t('settlementDateLabel')}</label>
                <input
                  type="date"
                  value={calc2.settlementDate}
                  onChange={(e) => setCalc2({...calc2, settlementDate: e.target.value})}
                  required
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t('dayCountLabel')}</label>
                <div style={styles.radioGroup}>
                  <div style={styles.radioOption}>
                    <input
                      type="radio"
                      id="dc1"
                      name="dayCount"
                      value="30/360"
                      checked={calc2.dayCount === '30/360'}
                      onChange={(e) => setCalc2({...calc2, dayCount: e.target.value})}
                    />
                    <label htmlFor="dc1" style={{margin: 0, fontWeight: 400, cursor: 'pointer'}}>30/360</label>
                  </div>
                  <div style={styles.radioOption}>
                    <input
                      type="radio"
                      id="dc2"
                      name="dayCount"
                      value="actual/360"
                      checked={calc2.dayCount === 'actual/360'}
                      onChange={(e) => setCalc2({...calc2, dayCount: e.target.value})}
                    />
                    <label htmlFor="dc2" style={{margin: 0, fontWeight: 400, cursor: 'pointer'}}>Actual/360</label>
                  </div>
                  <div style={styles.radioOption}>
                    <input
                      type="radio"
                      id="dc3"
                      name="dayCount"
                      value="actual/365"
                      checked={calc2.dayCount === 'actual/365'}
                      onChange={(e) => setCalc2({...calc2, dayCount: e.target.value})}
                    />
                    <label htmlFor="dc3" style={{margin: 0, fontWeight: 400, cursor: 'pointer'}}>Actual/365</label>
                  </div>
                  <div style={styles.radioOption}>
                    <input
                      type="radio"
                      id="dc4"
                      name="dayCount"
                      value="actual/actual"
                      checked={calc2.dayCount === 'actual/actual'}
                      onChange={(e) => setCalc2({...calc2, dayCount: e.target.value})}
                    />
                    <label htmlFor="dc4" style={{margin: 0, fontWeight: 400, cursor: 'pointer'}}>Actual/Actual</label>
                  </div>
                </div>
              </div>

              {error2 && <div style={styles.errorMessage}>{error2}</div>}

              <div style={styles.buttonGroup}>
                <button type="submit" style={styles.calculateBtn}>{t('calculateButton')}</button>
                <button type="button" onClick={handleClear2} style={styles.clearBtn}>{t('clearButton')}</button>
              </div>
            </form>
          </div>
        </div>

        {results1 && (
          <div style={styles.results}>
            <h3 style={styles.h2}>{t('results1Title')}</h3>
            <div style={styles.resultHighlight}>
              <p style={{margin: 0, color: '#334155', fontSize: '1rem', lineHeight: 1.6}}>
                {t('results1Text', {label: results1.label.toLowerCase()})}
              </p>
              <span style={styles.resultValue}>{results1.value}</span>
            </div>
          </div>
        )}

        {results2 && (
          <div style={styles.results}>
            <h3 style={styles.h2}>{t('results2Title')}</h3>
            <table style={styles.resultTable}>
              <tbody>
                <tr style={styles.resultTableRow}>
                  <td style={{...styles.resultTableCell, color: '#64748b', fontWeight: 500}}>{t('dirtyPriceLabel')}</td>
                  <td style={{...styles.resultTableCell, textAlign: 'right', color: '#1e293b', fontWeight: 600}}>{results2.dirtyPrice}</td>
                </tr>
                <tr style={styles.resultTableRow}>
                  <td style={{...styles.resultTableCell, color: '#64748b', fontWeight: 500}}>{t('cleanPriceLabel')}</td>
                  <td style={{...styles.resultTableCell, textAlign: 'right', color: '#1e293b', fontWeight: 600}}>{results2.cleanPrice}</td>
                </tr>
                <tr style={styles.resultTableRow}>
                  <td style={{...styles.resultTableCell, color: '#64748b', fontWeight: 500}}>{t('accruedInterestLabel')}</td>
                  <td style={{...styles.resultTableCell, textAlign: 'right', color: '#1e293b', fontWeight: 600}}>{results2.accruedInterest}</td>
                </tr>
                <tr style={{borderBottom: 'none'}}>
                  <td style={{...styles.resultTableCell, color: '#64748b', fontWeight: 500}}>{t('accruedDaysLabel')}</td>
                  <td style={{...styles.resultTableCell, textAlign: 'right', color: '#1e293b', fontWeight: 600}}>{results2.accruedDays}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('introTitle')}</h2>
        <p style={styles.paragraph}>{t('introParagraph1')}</p>
        <p style={styles.paragraph}>{t('introParagraph2')}</p>
        <p style={styles.paragraph}>{t('introParagraph3')}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('howToUseTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}><span style={styles.strong}>{t('step1Title')}</span> {t('step1Text')}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t('step2Title')}</span> {t('step2Text')}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t('step3Title')}</span> {t('step3Text')}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t('step4Title')}</span> {t('step4Text')}</li>
        </ol>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('insightsTitle')}</h2>
        <p style={styles.paragraph}>{t('insightsParagraph1')}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}><span style={styles.strong}>{t('insight1Title')}</span> {t('insight1Text')}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t('insight2Title')}</span> {t('insight2Text')}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t('insight3Title')}</span> {t('insight3Text')}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t('insight4Title')}</span> {t('insight4Text')}</li>
        </ul>
        <p style={styles.paragraph}>{t('insightsParagraph2')}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('detailsTitle')}</h2>
        <p style={styles.paragraph}>{t('detailsParagraph1')}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}><span style={styles.strong}>{t('detail1Title')}</span> {t('detail1Text')}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t('detail2Title')}</span> {t('detail2Text')}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t('detail3Title')}</span> {t('detail3Text')}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t('detail4Title')}</span> {t('detail4Text')}</li>
        </ul>
        <p style={styles.paragraph}>{t('detailsParagraph2')}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('faqTitle')}</h2>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq1Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq1Answer')}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq2Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq2Answer')}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq3Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq3Answer')}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq4Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq4Answer')}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq5Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq5Answer')}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('referencesTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>
            <a href="https://www.allinvestview.com/bond-calculator/" target="_blank" rel="noopener noreferrer" style={styles.refLink}>
              Bond Calculator - All Invest View
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://www.indiabonds.com/bonduni/blogs/what-is-bond-yield-calculator-and-its-benefits/" target="_blank" rel="noopener noreferrer" style={styles.refLink}>
              Bond Yield Calculator: Functionality & Advantages | IndiaBonds
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://www.treasurydirect.gov/savings-bonds/savings-bond-calculator/" target="_blank" rel="noopener noreferrer" style={styles.refLink}>
              Paper Savings Bond Calculator - TreasuryDirect
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://www.investor.gov/introduction-investing/investing-basics/glossary/savings-bond-calculator" target="_blank" rel="noopener noreferrer" style={styles.refLink}>
              Savings Bond Calculator | Investor.gov
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://www.calculator.net/bond-calculator.html" target="_blank" rel="noopener noreferrer" style={styles.refLink}>
              Bond Calculator - Calculator.net
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}